import { CHAT_ROOMS_AVATAR_DIAGRAM_DATA_KEY, ChatRoomsAvatarDiagramData } from '@y42/collaboration/chat';
import * as go from 'gojs';
import { genericToolTip } from '../../../../parts/generic-tooltip';
import { animate } from '../../../../utils/animate/animate';
import { fontModelToGojsData } from '../../utils/font-utils';
import { TEXT_FONT_PROP } from '../common/config/common-prop-names';
import { locationBinding, zOrderBindings } from '../common/utils/bindings';
import { PinNodeTemplateProps } from './models/pin-node-template-props';
import { geometry } from './utils/bindings';
import { calculatePinScale } from './utils/calculate-pin-scale';

const $ = go.GraphObject.make;
const animationDuration = 150;

const shape = ({ background, width, height }: PinNodeTemplateProps) => {
  return $(go.Shape, {
    stroke: null,
    name: 'shape',
    desiredSize: new go.Size(+width, +height),
    fill: background,
    ...geometry(),
  });
};

const getProfileImgMode = (
  data: { key?: string },
  part: go.Shape,
): {
  isProfileImg?: string;
  isProfileAvatar?: { initials: string; bgColor: string };
  isUnknown?: boolean;
} => {
  const uiId = data.key;
  const avatars: ChatRoomsAvatarDiagramData = part.diagram?.model.modelData[CHAT_ROOMS_AVATAR_DIAGRAM_DATA_KEY] || {
    chatAuthor: undefined,
    chatAvatars: {},
  };
  // avatar data is:
  // 1) "createdBy" for associated pinNode.key (uiId)
  // 2) or -> if there is already existing chat room (chatRoomId), but no "createdBy" -> creator is unknown
  // 3) in other cases currently logged in user is a creator (chatAuthor)
  const avatarData =
    (uiId &&
      (avatars.chatAvatars?.[uiId]?.createdBy ||
        (!!avatars.chatAvatars?.[uiId]?.chatRoomId && { isUnknown: true, name: '' }))) ||
    avatars.chatAuthor;

  if (avatarData?.imgSrc) {
    return { isProfileImg: avatarData.imgSrc };
  } else if (avatarData?.initials && avatarData?.bgColor) {
    return { isProfileAvatar: { initials: avatarData.initials, bgColor: avatarData.bgColor } };
  }

  return { isUnknown: true };
};

const avatarPicture = ({ width, height }: PinNodeTemplateProps) => {
  const AVATAR_PADDING = 10;
  const widthWithMargin = parseInt(width, 10) - AVATAR_PADDING;
  const heightWithMargin = parseInt(height, 10) - AVATAR_PADDING;

  const avatarImgVisibilityBinding = () =>
    new go.Binding('visible', '', (data: { key?: string }, part: go.Shape): boolean => {
      return !!getProfileImgMode(data, part).isProfileImg;
    });

  const avatarImgSourceBinding = () =>
    new go.Binding('source', '', (data: { key?: string }, part: go.Shape): string => {
      return getProfileImgMode(data, part).isProfileImg || '';
    });

  return $(
    go.Panel,
    'Spot',
    { isClipping: true },
    $(go.Shape, 'Circle', { width: widthWithMargin, height: heightWithMargin, strokeWidth: 0 }),
    $(
      go.Picture,
      {
        width: widthWithMargin,
        height: heightWithMargin,
        visible: false,
      },
      avatarImgVisibilityBinding(),
      avatarImgSourceBinding(),
    ),
  );
};

const avatarName = ({ width, height, textColor, font }: PinNodeTemplateProps) => {
  const AVATAR_PADDING = 10;
  const widthWithMargin = parseInt(width, 10) - AVATAR_PADDING;
  const heightWithMargin = parseInt(height, 10) - AVATAR_PADDING;

  const avatarNameVisibilityBinding = () =>
    new go.Binding('visible', '', (data: { key?: string }, part: go.Shape): boolean => {
      return !!getProfileImgMode(data, part).isProfileAvatar;
    });

  const avatarNameSourceBinding = () =>
    new go.Binding('text', '', (data: { key?: string }, part: go.Shape): string => {
      return getProfileImgMode(data, part).isProfileAvatar?.initials || '';
    });

  const avatarBgColorBiniding = () =>
    new go.Binding('background', '', (data: { key?: string }, part: go.Shape): string => {
      return getProfileImgMode(data, part).isProfileAvatar?.bgColor || '';
    });

  const gojsFont = fontModelToGojsData(font, TEXT_FONT_PROP);
  return $(
    go.Panel,
    'Spot',
    { isClipping: true },
    $(go.Shape, 'Circle', {
      width: widthWithMargin,
      height: heightWithMargin,
    }),
    $(
      go.TextBlock,
      {
        width: widthWithMargin,
        height: heightWithMargin,
        desiredSize: new go.Size(+widthWithMargin, +heightWithMargin),
        verticalAlignment: go.Spot.Center,
        textAlign: 'center',
        stroke: textColor,
        font: gojsFont[TEXT_FONT_PROP],
        background: '#DDDDDD',
        visible: false,
        toolTip: genericToolTip(
          {
            colors: {
              stroke: '#666666',
              fill: '#FFFFFF',
            },
          },
          [
            new go.Binding('text', '', (data: { key?: string }, part: go.Shape) => {
              return getProfileImgMode(data, part).isProfileAvatar?.initials || '';
            }),
          ],
        ),
      },
      avatarNameVisibilityBinding(),
      avatarNameSourceBinding(),
      avatarBgColorBiniding(),
    ),
  );
};

const avatarUnknown = ({ width, height, textColor, font }: PinNodeTemplateProps) => {
  const AVATAR_PADDING = 10;
  const widthWithMargin = parseInt(width, 10) - AVATAR_PADDING;
  const heightWithMargin = parseInt(height, 10) - AVATAR_PADDING;

  const avatarUnknownVisibilityBinding = () =>
    new go.Binding('visible', '', (data: { key?: string }, part: go.Shape): boolean => {
      return !!getProfileImgMode(data, part).isUnknown;
    });

  const avatarUnknownSourceBinding = () =>
    new go.Binding('text', '', (): string => {
      return '?';
    });

  const gojsFont = fontModelToGojsData(font, TEXT_FONT_PROP);
  return $(
    go.Panel,
    'Spot',
    { isClipping: true },
    $(go.Shape, 'Circle', {
      width: widthWithMargin,
      height: heightWithMargin,
    }),
    $(
      go.TextBlock,
      {
        width: widthWithMargin,
        height: heightWithMargin,
        desiredSize: new go.Size(+widthWithMargin, +heightWithMargin),
        verticalAlignment: go.Spot.Center,
        textAlign: 'center',
        stroke: textColor,
        font: gojsFont[TEXT_FONT_PROP],
        background: '#DDDDDD',
        visible: false,
        toolTip: genericToolTip(
          {
            colors: {
              stroke: '#666666',
              fill: '#FFFFFF',
            },
          },
          [
            new go.Binding('text', '', () => {
              return 'Unknown user';
            }),
          ],
        ),
      },
      avatarUnknownVisibilityBinding(),
      avatarUnknownSourceBinding(),
    ),
  );
};

const onMouseEnter = (_: go.InputEvent, obj: go.GraphObject) => {
  if (obj.part && obj.diagram) {
    animate([[obj.part, 'scale', obj.scale, calculatePinScale(obj.diagram) * 1.15]], {
      duration: animationDuration,
    });
  }
};

const onMouseLeave = (_: go.InputEvent, obj: go.GraphObject) => {
  if (obj.part && obj.diagram) {
    animate([[obj.part, 'scale', obj.scale, calculatePinScale(obj.diagram)]], {
      duration: animationDuration,
    });
  }
};

export const pinNodeTemplate = (templateProps: PinNodeTemplateProps) =>
  $(
    go.Node,
    go.Panel.Auto,
    {
      isLayoutPositioned: false,
      locationSpot: go.Spot.Center,
      toolTip: null,
      isShadowed: true,
      shadowColor: '#0000001A',
      shadowOffset: new go.Point(0, 5),
      shadowBlur: 15,
      mouseEnter: onMouseEnter,
      mouseLeave: onMouseLeave,
    },
    locationBinding(),
    shape(templateProps),
    avatarPicture(templateProps),
    avatarName(templateProps),
    avatarUnknown(templateProps),
    ...zOrderBindings(),
  );
